import React, { useState, useEffect, useRef } from "react";
import FileSaver from "file-saver";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { FaFileCsv, FaFilePdf, FaFileExcel } from "react-icons/fa6";
import FileExport from "../../../Assets/Images/CommanImages/fileexport.png";
import Filter from "../../../Assets/Images/CommanImages/filter.png";
import columnaddition from "../../../Assets/Images/CommanImages/rectangleplus.png";
import searchicon from "../../../Assets/Images/CommanImages/searchyellow.png";
import updown from "../../../Assets/Images/CommanImages/updown.png";
import print from "../../../Assets/Images/CommanImages/print.png";
import cross from "../../../Assets/Images/CommanImages/cross_cancel.png";
import upimg from "../../../Assets/Images/CommanImages/up_arrow.png";
import downimg from "../../../Assets/Images/CommanImages/down_arrow.png";
import leftArrow from "../../../Assets/Images/CommanImages/Larrowbar.png";
import rightArrow from "../../../Assets/Images/CommanImages/Rarrowbar.png";
import reportImg from "../../../Assets/Images/client_logos/Reports/report_logo.png";
import watermarkImg from "../../../Assets/Images/client_logos/Reports/water_mark_img.png";
import SelectedValuesDropdown from "../../../Components/Shared/SelectedValuesDropdown/SelectDropdown";

const PDCSReportsTable = ({
  headers,
  data,
  searchPlaceholder,
  children,
  isColumnDivVisible,
  setIsColumnDivVisible,
  dataClient,
  isExpanded,
  ReportPageHeader,
  course, batch, student, subject, dppName, date, chapter, test, feedback
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const userName = dataClient?.client_schema_name?.[6]?.[0]?.user_name || "Unknown User";
  const [isFilterVisible, setIsFilterVisible] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isSearchModalVisible, setIsSearchModalVisible] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [currentSearchIndex, setCurrentSearchIndex] = useState(0);
  const modalRef = useRef(null);
  const columnDivRef = useRef(null);

  const [filters, setFilters] = useState({});
  const [appliedFilters, setAppliedFilters] = useState({});
  const [isFiltersApplied, setIsFiltersApplied] = useState(false);
  const [isFilterDropdownSelected, setIsFilterDropdownSelected] = useState(false);

  // ── Locked column: S.No can not be selected / unselected ──
  const isLockedColumn = (column) => column === "S.No";

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setIsModalVisible(false);
      }
      if (
        columnDivRef.current &&
        !columnDivRef.current.contains(event.target)
      ) {
        setIsColumnDivVisible(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [setIsModalVisible, setIsColumnDivVisible]);

  useEffect(() => {
    if (headers.length > 0 && visibleColumns.length === 0) {
      setVisibleColumns(headers.slice(0, 7));
    }
  }, [headers, visibleColumns]);

  const sortedData = React.useMemo(() => {
    let filteredData = data.filter((row) => {
      const matchesSearchQuery = row.some(
        (cell) =>
          cell && String(cell).toLowerCase().includes(searchQuery.toLowerCase())
      );

      const matchesFilters = Object.entries(appliedFilters).every(
        ([key, value]) => {
          const colIndex = headers.indexOf(key);
          return value ? row[colIndex] === value : true;
        }
      );

      return matchesSearchQuery && matchesFilters;
    });

    if (sortConfig.key !== null) {
      filteredData.sort((a, b) => {
        const colIndex = headers.indexOf(sortConfig.key);
        if (a[colIndex] < b[colIndex]) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (a[colIndex] > b[colIndex]) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }

    return filteredData;
  }, [data, sortConfig, headers, searchQuery, appliedFilters]);

  const handleColumnSelection = (column) => {
    if (isLockedColumn(column)) return; // S.No locked
    if (visibleColumns.includes(column)) {
      setVisibleColumns(visibleColumns.filter((col) => col !== column));
    } else {
      setVisibleColumns([...visibleColumns, column]);
    }
  };

  const handleSort = (column) => {
    let direction = "asc";
    if (sortConfig.key === column && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key: column, direction });
  };

  const handleExportClick = () => {
    if (data.length > 0) {
      setIsModalVisible(true);
    }
  };

  const handleColumnDivClick = () => {
    if (data.length > 0) {
      setIsColumnDivVisible(!isColumnDivVisible);
    }
  };

  const handleApplyFilter = () => {
    if (Object.keys(filters).length > 0) {
      setAppliedFilters({ ...filters });
      setCurrentPage(1);
      setIsFiltersApplied(true);
      setIsFilterVisible(false);
    }
  };

  const handleResetFilter = () => {
    setFilters({});
    setAppliedFilters({});
    setIsFiltersApplied(false);
  };

  // ── Export ──
  const handleExport = (type) => {
    const exportColumns = visibleColumns;
    const filteredExportData = sortedData.map((row) =>
      exportColumns.map((column) => row[headers.indexOf(column)])
    );

    if (type === "excel" || type === "csv") {
      const worksheet = XLSX.utils.aoa_to_sheet([
        exportColumns,
        ...filteredExportData,
      ]);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

      const fileExtension = type === "csv" ? "csv" : "xlsx";
      const mimeType =
        type === "csv"
          ? "text/csv"
          : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

      const file = XLSX.write(workbook, {
        bookType: fileExtension,
        type: "array",
      });
      const blob = new Blob([file], { type: mimeType });
      FileSaver.saveAs(blob, `${ReportPageHeader}.${fileExtension}`);
    } else if (type === "pdf") {
      const doc = new jsPDF();
      doc.autoTable({
        head: [exportColumns],
        body: filteredExportData,
      });
      doc.save(`${ReportPageHeader}.pdf`);
    }
  };

  // ── Print ──
  const handleSaveAndPrint = () => {
    const exportData = sortedData.map((row) =>
      visibleColumns.map((column) => row[headers.indexOf(column)])
    );

    const currentDate = new Date();
    const formattedDate = currentDate.toLocaleDateString("en-GB", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
    const formattedTime = currentDate.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
    const formattedDateTime = `${formattedDate}, ${formattedTime}`;

    const availablePageHeight = 630;
    const headerHeight = 150;
    const footerHeight = 50;

    const rowHeight = 20;

    const pageHeightForData = availablePageHeight - headerHeight - footerHeight;
    const rowsPerPage = Math.floor(pageHeightForData / rowHeight);

    const totalPages = Math.ceil(sortedData.length / rowsPerPage);

    const printSection = document.createElement("div");
    printSection.id = "printSection";

    let printContent = `
      <style>
        body { margin: 0; padding: 0; font-family: 'Calibri', sans-serif; }
        table { width: 100%; border-collapse: collapse; table-layout: fixed; background-color: rgba(255,255,255,0.5); font-size: 10px; margin-top: 6mm; }
        th, td { border: 1px solid black; padding: 4px; text-align: center; word-wrap: break-word; font-size: 10px; word-break: break-all; }
        th { font-size: 11px; font-weight: bold; background-color: #f2f2f2; }
        .Heading-tc { background-color: #f2f2f2; }
        .header-container { display: flex; justify-content: space-between; align-items: end; margin-bottom: 14px; border-bottom: 1.6px solid #424242; }
        .center-header { text-align: center; font-size: 18px; font-weight: bold; flex-grow: 1; color: #424242; }
        .logo { width: 140px; height: auto; margin-top: 10px; }
        .right-info { font-size: 10px; }
        .username { text-transform: uppercase; }
        .title-container { display: flex; justify-content: space-between; margin-top: 8px; }
        .title-container p { margin: 2px 0; }
        .title-label { color: #424242; font-size: 12px; font-weight: 600; }
        .course-title { font-size: 12px; color: #424242; }
        .transparent-table { background-color: rgba(255,255,255,0.5); }
        .watermark { position: fixed; top: 50%; left: 50%; transform: translate(-50%,-50%); z-index: -1; pointer-events: none; opacity: 0.1; }
        .watermark img { width: auto; height: 200px; }
        .page-block { page-break-after: always; page-break-inside: avoid; }
        .page-block:last-child { page-break-after: auto; }
        @media print {
          body * { visibility: hidden; }
          #printSection, #printSection * { visibility: visible; }
          #printSection { position: absolute; left: 0; top: 0; width: 100%; padding: 10mm; }
          .page-block { page-break-after: always; page-break-inside: avoid; }
          .page-block:last-child { page-break-after: auto; }
        }
        @page { size: auto; margin: 5mm; }
      </style>
    `;

    printContent += `<div class="watermark"><img src="${watermarkImg}" alt="Watermark" /></div>`;

    const buildHeader = (pageNum) => `
      <div class="header-container">
        <img class="logo" src="${reportImg}" alt="Logo" />
        <h1 class="center-header">${ReportPageHeader}</h1>
        <div class="right-info">
          <p>Print Date&nbsp;&nbsp;: ${formattedDateTime}</p>
          <p>Printed By&nbsp;: <span class="username">${userName}</span></p>
          <p>Page ${pageNum} of ${totalPages}</p>
        </div>
      </div>
    `;
    const tableHeader = `
      <thead>
        <tr>${visibleColumns
          .map((heading) => `<th class="Heading-tc">${heading}</th>`)
          .join("")}</tr>
      </thead>
    `;

    for (let page = 1; page <= totalPages; page++) {
      const startIndex = (page - 1) * rowsPerPage;
      const endIndex = Math.min(startIndex + rowsPerPage, sortedData.length);
      const pageExportRows = exportData.slice(startIndex, endIndex);

      const isFirstPage = page === 1;

      printContent += `
        <div class="page-block">
          ${buildHeader(page)}
          ${
            isFirstPage
              ? `
          <div class="title-container">
            <div class="left-section">
              <p><span class="title-label">Course:</span> <span class="course-title">${course}</span></p>
              <p><span class="title-label">Batch:</span> <span class="course-title">${batch}</span></p>
              ${subject ? `<p><span class="title-label">Subject:</span> <span class="course-title">${subject}</span></p>` : ""}
            </div>
            <div class="right-section">
              ${chapter ? `<p><span class="title-label">Chapter:</span> <span class="course-title">${chapter}</span></p>` : ""}
              ${student ? `<p><span class="title-label">Student:</span> <span class="course-title">${student}</span></p>` : ""}
              ${dppName ? `<p><span class="title-label">DPP Name:</span> <span class="course-title">${dppName}</span></p>` : ""}
              ${feedback ? `<p><span class="title-label">Feedback:</span> <span class="course-title">${feedback}</span></p>` : ""}
              ${test ? `<p><span class="title-label">Test:</span> <span class="course-title">${test}</span></p>` : ""}
              ${date ? `<p><span class="title-label">Date:</span> <span class="course-title">${date}</span></p>` : ""}
            </div>
          </div>`
              : ""
          }
          <table class="transparent-table">
            ${tableHeader}
            <tbody>
              ${pageExportRows
                .map(
                  (exportRow) =>
                    `<tr>${exportRow
                      .map(
                        (cell) =>
                          `<td style="text-align:left;padding:4px;">${
                            cell !== null && cell !== undefined ? cell : ""
                          }</td>`
                      )
                      .join("")}</tr>`
                )
                .join("")}
            </tbody>
          </table>
        </div>
      `;
    }

    printSection.innerHTML = printContent;
    document.body.appendChild(printSection);

    const printStyles = document.createElement("style");
    printStyles.innerHTML = `
      @media print {
        body * { visibility: hidden; }
        #printSection, #printSection * { visibility: visible; }
        #printSection { position: absolute; left: 0; top: 0; width: 100%; padding: 10mm; }
      }
      @page { size: auto; margin: 5mm; }
    `;
    document.head.appendChild(printStyles);

    const logo = new Image();
    logo.src = reportImg;
    logo.onload = () => {
      window.print();
      document.body.removeChild(printSection);
      document.head.removeChild(printStyles);
    };
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const totalPages = Math.ceil(sortedData.length / pageSize);
  const paginatedData = React.useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return sortedData.slice(startIndex, startIndex + pageSize);
  }, [sortedData, currentPage, pageSize]);

  const renderPagination = () => {
    const pages = [];
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(
          <button
            key={i}
            onClick={() => handlePageChange(i)}
            style={{ borderRadius: "5px" }}
            className={i === currentPage ? "active" : ""}
          >
            {i}
          </button>
        );
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 3; i++) {
          pages.push(
            <button
              key={i}
              style={{ borderRadius: "5px" }}
              onClick={() => handlePageChange(i)}
              className={i === currentPage ? "active" : ""}
            >
              {i}
            </button>
          );
        }
        pages.push(<span style={{ borderRadius: "5px" }} key="dots1">...</span>);
        pages.push(
          <button key={totalPages} onClick={() => handlePageChange(totalPages)} style={{ borderRadius: "5px" }}>
            {totalPages}
          </button>
        );
      } else if (currentPage > 3 && currentPage < totalPages - 2) {
        pages.push(
          <button key={1} onClick={() => handlePageChange(1)} style={{ borderRadius: "5px" }}>1</button>
        );
        pages.push(<span style={{ borderRadius: "5px" }} key="dots1">...</span>);
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(
            <button
              key={i}
              onClick={() => handlePageChange(i)}
              style={{ borderRadius: "5px" }}
              className={i === currentPage ? "active" : ""}
            >
              {i}
            </button>
          );
        }
        pages.push(<span style={{ borderRadius: "5px" }} key="dots2">...</span>);
        pages.push(
          <button key={totalPages} onClick={() => handlePageChange(totalPages)} style={{ borderRadius: "5px" }}>
            {totalPages}
          </button>
        );
      } else {
        pages.push(
          <button key={1} onClick={() => handlePageChange(1)} style={{ borderRadius: "5px" }}>1</button>
        );
        pages.push(<span style={{ borderRadius: "5px" }} key="dots1">...</span>);
        for (let i = totalPages - 2; i <= totalPages; i++) {
          pages.push(
            <button
              key={i}
              onClick={() => handlePageChange(i)}
              style={{ borderRadius: "5px" }}
              className={i === currentPage ? "active" : ""}
            >
              {i}
            </button>
          );
        }
      }
    }
    return pages;
  };

  const handleSearch = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    setCurrentPage(1);

    if (query) {
      const results = [];
      data.forEach((row, rowIndex) => {
        row.forEach((cell, cellIndex) => {
          if (
            cell !== null &&
            cell !== undefined &&
            cell.toString().toLowerCase().includes(query.toLowerCase()) &&
            visibleColumns.includes(headers[cellIndex])
          ) {
            results.push({ rowIndex, cellIndex });
          }
        });
      });
      setSearchResults(results);
      setCurrentSearchIndex(0);
    } else {
      setSearchResults([]);
    }
    setIsSearchModalVisible(true);
  };

  const highlightText = (text, highlight, isCurrent) => {
    if (!highlight) return text;
    const textStr = text != null ? text.toString() : "";
    const regex = new RegExp(`(${highlight})`, "gi");
    return textStr.split(regex).map((part, index) =>
      part.toLowerCase() === highlight.toLowerCase() ? (
        <span key={index} style={{ backgroundColor: isCurrent ? "yellow" : "#FFD700" }}>
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  const moveToNextResult = () => {
    if (searchResults.length === 0) return;
    setCurrentSearchIndex((prevIndex) => {
      const nextIndex = prevIndex + 1 < searchResults.length ? prevIndex + 1 : prevIndex;
      const nextResult = searchResults[nextIndex];
      const nextPage = Math.floor(nextResult.rowIndex / pageSize) + 1;
      if (nextPage !== currentPage) setCurrentPage(nextPage);
      return nextIndex;
    });
  };

  const moveToPreviousResult = () => {
    if (searchResults.length === 0) return;
    setCurrentSearchIndex((prevIndex) => {
      const prevIndexAdjusted = prevIndex - 1 >= 0 ? prevIndex - 1 : prevIndex;
      const prevResult = searchResults[prevIndexAdjusted];
      const prevPage = Math.floor(prevResult.rowIndex / pageSize) + 1;
      if (prevPage !== currentPage) setCurrentPage(prevPage);
      return prevIndexAdjusted;
    });
  };

  return (
    <div>
      <div>
        {/* ── Record count & page-size selector ── */}
        <div
          className="search-and-pagination-wrapper"
          style={{ display: "flex", alignItems: "center", justifyContent: "end" }}
        >
          <div
            className="search-wrapper"
            style={{ display: "flex", alignItems: "center", justifyContent: "end" }}
          >
            <div className="pagination-info">
              Displaying{" "}
              {Math.min((currentPage - 1) * pageSize + 1, sortedData.length)} -{" "}
              {Math.min(currentPage * pageSize, sortedData.length)} of{" "}
              {sortedData.length} records |
              {["10", "25", "50", "100", "500"].map((size) => (
                <span
                  key={size}
                  onClick={() => handlePageSizeChange(Number(size))}
                  style={{
                    cursor: "pointer",
                    margin: "0 5px",
                    textDecoration: size === pageSize.toString() ? "none" : "underline",
                    color: size === pageSize.toString() ? "black" : "#337AB7",
                  }}
                >
                  {size}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* ── Toolbar ── */}
        <div
          className="row-tc"
          style={{
            backgroundColor: "#D6D6D6",
            marginTop: "5px",
            borderTop: "1px solid grey",
            borderBottom: "1px solid grey",
          }}
        >
          {/* Advance Filter */}
          <div
            className="col-cs2-TABLE"
            style={{
              backgroundColor: "#FFBD20",
              display: "flex",
              alignItems: "center",
              cursor: data.length > 0 ? "pointer" : "not-allowed",
              paddingLeft: "8px",
            }}
            onClick={() => data.length > 0 && setIsFilterVisible(!isFilterVisible)}
          >
            <img src={Filter} style={{ height: "25px", width: "25px" }} alt="filter" />
            <span style={{ marginLeft: "10px", color: "#2F2E40" }}>Advance Filter</span>
          </div>

          {/* Export */}
          <div
            className="col-cs"
            style={{
              display: "flex",
              alignItems: "center",
              cursor: data.length > 0 ? "pointer" : "not-allowed",
            }}
          >
            <div style={{ display: "flex", alignItems: "center" }} onClick={handleExportClick}>
              <img src={FileExport} style={{ height: "25px", width: "25px" }} alt="export" />
              <span style={{ marginLeft: "10px", color: "#2F2E40" }}>Export</span>
            </div>
            {isModalVisible && (
              <div ref={modalRef} style={{ position: "relative", display: "inline-block" }}>
                <div className="arrow-up-tc-2"></div>
                <div className="modal-tc-2">
                  <div className="modal-content-tc-2">
                    <button onClick={() => [handleExport("excel"), setIsModalVisible(false)]}>
                      <FaFileExcel className="Img-EXCEL" />
                      <span style={{ fontSize: "14px" }}>Excel</span>
                    </button>
                    <button onClick={() => [handleExport("csv"), setIsModalVisible(false)]}>
                      <FaFileCsv className="Img-CSV" />
                      <span style={{ fontSize: "14px" }}>CSV</span>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Print */}
            <div
              className="col-cs"
              style={{
                display: "flex",
                alignItems: "center",
                cursor: data.length > 0 ? "pointer" : "not-allowed",
              }}
            >
              <div
                onClick={() => data.length > 0 && [handleSaveAndPrint(), setIsModalVisible(false)]}
                style={{ display: "flex", alignItems: "center" }}
              >
                <img src={print} alt="print" />
                <span style={{ marginLeft: "10px", color: "#2F2E40" }}>Print</span>
              </div>
            </div>
          </div>

          {/* Search toggle */}
          <div
            className="col-tc"
            onClick={() => setIsSearchModalVisible(!isSearchModalVisible)}
          >
            <img src={searchicon} style={{ height: "25px", width: "25px" }} alt="search" />
            <span style={{ marginLeft: "10px", color: "#2F2E40" }}>Search</span>
          </div>

          {isSearchModalVisible && (
            <div>
              <div className="inside-search-table-items">
                <div className="inside-table-search-items">
                  <div className="search-input-container">
                    <input
                      type="text"
                      placeholder="Search..."
                      name="search"
                      value={searchQuery}
                      onChange={handleSearch}
                    />
                  </div>
                  <div className="search-results-container">
                    <span style={{ width: "65px" }}>
                      {searchResults.length > 0
                        ? `${currentSearchIndex + 1} of ${searchResults.length}`
                        : ""}
                    </span>
                  </div>
                  <div className="button-container">
                    <img
                      src={upimg}
                      alt="up"
                      onClick={moveToPreviousResult}
                      style={{
                        filter:
                          currentSearchIndex > 0
                            ? "invert(48%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(75%) contrast(90%)"
                            : "brightness(0.5) invert(0.5)",
                        cursor: currentSearchIndex > 0 ? "pointer" : "not-allowed",
                      }}
                      disabled={searchResults.length === 0 || currentSearchIndex === 0}
                    />
                    <img
                      src={downimg}
                      alt="down"
                      onClick={moveToNextResult}
                      style={{
                        filter:
                          currentSearchIndex < searchResults.length - 1
                            ? "invert(48%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(75%) contrast(90%)"
                            : "brightness(0.5) invert(0.5)",
                        cursor:
                          currentSearchIndex < searchResults.length - 1
                            ? "pointer"
                            : "not-allowed",
                      }}
                      disabled={
                        searchResults.length === 0 ||
                        currentSearchIndex === searchResults.length - 1
                      }
                    />
                    <span
                      onClick={() => {
                        setSearchQuery("");
                        setSearchResults([]);
                        setCurrentSearchIndex(0);
                        setIsSearchModalVisible(false);
                      }}
                    >
                      <img src={cross} alt="cross" />
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Column Selection */}
          <div
            ref={columnDivRef}
            className="col-tc"
            style={{
              textAlign: "end",
              marginRight: "8px",
              display: "flex",
              alignItems: "center",
              position: "relative",
              cursor: data.length > 0 ? "pointer" : "not-allowed",
            }}
            onClick={handleColumnDivClick}
          >
            <img src={columnaddition} style={{ height: "25px", width: "25px" }} alt="column addition" />
            <span style={{ marginLeft: "10px", color: "#2F2E40" }}>Column Selection</span>

            {isColumnDivVisible && (
              <>
                <div className="arrow-up-tc"></div>
                <div className="popupColumn-tc">
                  {headers.map((column, index) => {
                    const locked = isLockedColumn(column);
                    return (
                      <div
                        key={index}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleColumnSelection(column);
                        }}
                        style={{
                          cursor: locked ? "not-allowed" : "pointer",
                          opacity: locked ? 0.6 : 1,
                        }}
                      >
                        <input
                          type="checkbox"
                          id={`column-${index}`}
                          name="columnSelection"
                          value={column}
                          className="square-checkbox-tc"
                          checked={visibleColumns.includes(column)}
                          disabled={locked}
                          readOnly
                        />
                        <label
                          htmlFor={`column-${index}`}
                          style={{
                            marginLeft: "10px",
                            maxWidth: "110px",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "inline-block",
                            cursor: locked ? "not-allowed" : "pointer",
                          }}
                          title={column}
                        >
                          {column}
                        </label>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>

        {/* ── Table ── */}
        <div
          className="table-wrapper"
          style={{
            maxHeight: isExpanded ? "64vh" : "52vh",
            overflowY: "auto",
            overflowX: "auto",
            minWidth: "100%",
            maxWidth: "82.2vw",
            marginRight: "auto",
          }}
        >
          {data.length === 0 ? (
            <div style={{ textAlign: "center", marginTop: "20px" }}></div>
          ) : (
            <table className="table-tc" style={{ width: "100%" }}>
              <thead>
                <tr>
                  {visibleColumns.map((heading, index) => (
                    <th
                      onClick={() => handleSort(heading)}
                      className="Heading-tc"
                      key={index}
                      style={{
                        textAlign: "center",
                        cursor: "pointer",
                        position: "sticky",
                        top: "0",
                        zIndex: 2,
                        backgroundColor: "white",
                        padding: "8px",
                      }}
                    >
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "left",
                        }}
                      >
                        {heading}
                        <img
                          src={updown}
                          style={{ height: "20px", width: "20px", marginLeft: "5px" }}
                          alt="sort"
                        />
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginatedData.map((row, rowIndex) => (
                  <tr key={rowIndex} className={rowIndex % 2 === 1 ? "even-row-tc" : ""}>
                    {visibleColumns.map((column) => {
                      const cellIndex = headers.indexOf(column);
                      const cellValue = row[cellIndex];

                      return (
                        <td key={column} style={{ textAlign: "left", color: "#6A6A6A" }}>
                          {highlightText(
                            cellValue,
                            searchQuery,
                            searchResults.length > 0 &&
                              searchResults[currentSearchIndex]?.rowIndex === rowIndex &&
                              searchResults[currentSearchIndex]?.cellIndex === cellIndex
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* ── Pagination ── */}
        {data.length > 0 && totalPages > 1 && (
          <div className="pagination-tc">
            <button
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1}
              className="extreme-left-tc-pagination"
            >
              <img src={leftArrow} alt="left arrow" />
            </button>
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="lefttc-pagination"
            >
              {"<"}
            </button>
            {renderPagination()}
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="righttc-pagination"
            >
              {">"}
            </button>
            <button
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage === totalPages}
              className="extreme-right-tc-pagination"
            >
              <img src={rightArrow} alt="right arrow" />
            </button>
          </div>
        )}

        {/* ── Advance Filter Sidebar ── */}
        {isFilterVisible && (
          <div className="filter-sidebar-tc">
            <div className="filter-content-tc" style={{ position: "relative" }}>
              <button
                className="repman-cross-btn-HOD"
                onClick={() => setIsFilterVisible(false)}
              >
                <p>X</p>
              </button>
              <h3 className="AdvanceF-heading-tc">Filter</h3>
              <div className="filter-apply-reset-ques-rev-dash">
                <button className="create-test-section-btn" onClick={handleApplyFilter}>
                  APPLY
                </button>
                {isFiltersApplied && (
                  <button className="create-test-section-btn" onClick={handleResetFilter}>
                    RESET
                  </button>
                )}
              </div>
              <div className="filter-options-container">
                {headers.map((header, index) => {
                  const colIdx = headers.indexOf(header);
                  let uniqueOptions = [
                    ...new Set(
                      data.map((row) => row[colIdx]).filter((option) => option)
                    ),
                  ];
                  uniqueOptions.sort((a, b) => (a > b ? 1 : -1));
                  return (
                    <SelectedValuesDropdown
                      key={index}
                      label={header}
                      width="300px"
                      marginLeft="15px"
                      marginTop="18px"
                      setSelected={(selectedOption) => {
                        setFilters((prevFilters) => ({
                          ...prevFilters,
                          [header]: selectedOption,
                        }));
                      }}
                      options={uniqueOptions.map((value) => ({
                        label: value,
                        id: value,
                      }))}
                      selected={filters[header] || ""}
                      Height="auto"
                      OverFlow="auto"
                      minheight="200px"
                    />
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PDCSReportsTable;